/**
 * @fileoverview Pharmacist Dashboard page component
 * Displays statistics, quick access buttons, and alerts for pharmacist users
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchData, intl, HTTP_STATUS } from "dmsbasereact";

import { DashboardLayout } from "../../components/Dashboard";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { APP_COLORS } from "../../theme/appTheme";
import { useReturnReasons } from "../../constants/ReturnReasons";

// MUI Icons
import VaccinesIcon from "@mui/icons-material/Vaccines";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SettingsIcon from "@mui/icons-material/Settings";

/**
 * PharmacistDashboard - Dashboard page for pharmacist users
 * @returns {JSX.Element} The pharmacist dashboard with stats, quick access, and alerts
 */
export const PharmacistDashboard = () => {
    const navigate = useNavigate();
    const ReturnReasons = useReturnReasons();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        stats: null,
        alerts: null,
        pharmacistName: null,
        documentStatus: null,
    });

    /**
     * Fetch all dashboard data from API endpoints
     */
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const [checkResponse, statsResponse, alertsResponse, documentsResponse] = await Promise.all([
                fetchData("/dashboard/pharmacist/check", "GET", true),
                fetchData("/dashboard/pharmacist/stats", "GET", true),
                fetchData("/dashboard/pharmacist/alerts", "GET", true),
                fetchData("/account/documents/pending-count", "GET", true).catch(() => null),
            ]);

            const newData = { stats: null, alerts: null, pharmacistName: null, documentStatus: null };

            if (checkResponse.status === HTTP_STATUS.OK_200) {
                newData.pharmacistName = checkResponse.data?.data?.pharmacist_name;
            }

            if (statsResponse.status === HTTP_STATUS.OK_200) {
                newData.stats = statsResponse.data?.data;
            }

            if (alertsResponse.status === HTTP_STATUS.OK_200) {
                newData.alerts = alertsResponse.data?.data;
            }

            if (documentsResponse?.status === HTTP_STATUS.OK_200) {
                newData.documentStatus = documentsResponse.data?.data;
            }

            setDashboardData(newData);
        } catch (err) {
            console.error("Error fetching pharmacist dashboard data:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    /**
     * Format pending returns alerts
     */
    const formatPendingReturnsAlerts = () => {
        if (!dashboardData.alerts?.pending_returns) return [];
        return dashboardData.alerts.pending_returns.map((item) => {
            const date = item.created_on ? new Date(item.created_on).toLocaleDateString('pt-BR') : '';
            const translatedReason = item.reason_name && ReturnReasons[item.reason_name]
                ? ReturnReasons[item.reason_name]
                : (item.reason || intl.get("Return").d("Return"));
            return {
                id: item.return_pk,
                title: `#${item.return_pk} - ${translatedReason}`,
                description: `${item.quantity} ${intl.get("Capsules").d("capsules")}${date ? ` - ${date}` : ''}`,
                return_pk: item.return_pk,
            };
        });
    };

    /**
     * Format recent dispensations alerts
     */
    const formatRecentDispensationsAlerts = () => {
        if (!dashboardData.alerts?.recent_dispensations) return [];
        return dashboardData.alerts.recent_dispensations.map((item) => ({
            id: item.dispensing_pk,
            title: item.patient_name,
            description: item.created_on ? new Date(item.created_on).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-',
            dispensation_pk: item.dispensing_pk,
        }));
    };

    /**
     * Handle click on pending return alert
     */
    const handleReturnClick = (item) => {
        if (item?.return_pk) {
            navigate(APP_ROUTES.Return);
        }
    };

    /**
     * Handle click on recent dispensation alert
     */
    const handleDispensationClick = (item) => {
        if (item?.dispensation_pk) {
            navigate(APP_ROUTES.DispensingHistory);
        }
    };

    /**
     * Handle click on document alert - navigate to profile documents tab
     */
    const handleDocumentClick = () => {
        navigate(APP_ROUTES.ProfileSettings);
    };

    /**
     * Format document alerts
     */
    const formatDocumentAlerts = () => {
        if (!dashboardData.documentStatus?.needs_action) return [];
        const rejectedCount = dashboardData.documentStatus?.rejected || 0;
        if (rejectedCount > 0) {
            return [{
                id: "rejected-docs",
                title: intl.get("RejectedDocuments").d("Rejected Documents"),
                description: intl.get("RejectedDocumentsMessage", { count: rejectedCount }).d("{count} document(s) need to be re-uploaded"),
            }];
        }
        return [];
    };

    // Stats configuration
    const stats = [
        {
            title: intl.get("TotalDispensations").d("Total Dispensations"),
            value: dashboardData.stats?.total_dispensations || 0,
            icon: VaccinesIcon,
            color: APP_COLORS.primary.main,
            onClick: () => navigate(APP_ROUTES.Dispensing),
        },
        {
            title: intl.get("ThisMonth").d("This Month"),
            value: dashboardData.stats?.dispensations_this_month || 0,
            icon: EventAvailableIcon,
            color: APP_COLORS.system.success,
            subtitle: intl.get("Dispensations").d("Dispensations"),
        },
        {
            title: intl.get("PendingReturns").d("Pending Returns"),
            value: dashboardData.stats?.pending_returns || 0,
            icon: AssignmentReturnIcon,
            color: APP_COLORS.system.warning,
            onClick: () => navigate(APP_ROUTES.Return),
        },
        {
            title: intl.get("CompletedReturns").d("Completed Returns"),
            value: dashboardData.stats?.completed_returns_this_month || 0,
            icon: CheckCircleIcon,
            color: APP_COLORS.system.info,
            subtitle: intl.get("ThisMonth").d("This Month"),
        },
    ];

    // Quick access buttons configuration
    const quickAccess = [
        {
            label: intl.get("Dispensation").d("Dispensation"),
            icon: VaccinesIcon,
            route: APP_ROUTES.Dispensing,
            color: APP_COLORS.primary.main,
        },
        {
            label: intl.get("Return").d("Return"),
            icon: AssignmentReturnIcon,
            route: APP_ROUTES.Return,
            color: APP_COLORS.secondary.main,
        },
        {
            label: intl.get("TrainingVideo").d("Training Video"),
            icon: OndemandVideoIcon,
            route: APP_ROUTES.TrainingVideo,
            color: APP_COLORS.system.info,
        },
        {
            label: intl.get("ProfileSettings").d("Profile Settings"),
            icon: SettingsIcon,
            route: APP_ROUTES.ProfileSettings,
            color: APP_COLORS.system.warning,
        },
    ];

    // Alerts configuration
    const alerts = [];

    // Add document alert first if there are rejected documents
    if (dashboardData.documentStatus?.needs_action) {
        alerts.push({
            title: intl.get("DocumentsNeedAttention").d("Documents Need Attention"),
            items: formatDocumentAlerts(),
            severity: "error",
            emptyMessage: "",
            onItemClick: handleDocumentClick,
        });
    }

    alerts.push(
        {
            title: intl.get("PendingReturnsCard").d("Pending Returns"),
            items: formatPendingReturnsAlerts(),
            severity: "warning",
            emptyMessage: intl.get("NoPendingReturns").d("No pending returns"),
            onItemClick: handleReturnClick,
        },
        {
            title: intl.get("RecentDispensations").d("Recent Dispensations"),
            items: formatRecentDispensationsAlerts(),
            severity: "info",
            emptyMessage: intl.get("NoRecentDispensations").d("No recent dispensations"),
            onItemClick: handleDispensationClick,
        }
    );

    return (
        <DashboardLayout
            welcomeName={dashboardData.pharmacistName}
            loading={loading}
            stats={stats}
            quickAccess={quickAccess}
            alerts={alerts}
        />
    );
};
